import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

// TENANT ISOLATION: Global data version - increments on tenant switch to force refetch
let globalDataVersion = 0;

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

// TENANT ISOLATION: Hook for components to respond to tenant changes
export const useDataVersion = () => {
  const [version, setVersion] = useState(globalDataVersion);
  
  useEffect(() => {
    const handleTenantClear = () => {
      setVersion(++globalDataVersion);
    };
    
    window.addEventListener('tenantDataClear', handleTenantClear);
    return () => window.removeEventListener('tenantDataClear', handleTenantClear);
  }, []);
  
  return version;
};

export const TenantProvider = ({ children }) => {
  const { user, isAuthenticated, isPlatformAdmin } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [features, setFeatures] = useState({});
  const [limits, setLimits] = useState({});
  const [hierarchyConfig, setHierarchyConfig] = useState({ enable_story_layer: true });
  const [loading, setLoading] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);
  
  // TENANT ISOLATION: Track active tenant context (impersonated or user's actual)
  const [activeTenantId, setActiveTenantId] = useState(null);
  const [activeTenantName, setActiveTenantName] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Listen for tenant context changes (from TenantContextIndicator)
  const handleTenantContextChange = useCallback(() => {
    const impersonatedId = localStorage.getItem('impersonated_tenant_id');
    const impersonatedName = localStorage.getItem('impersonated_tenant_name');
    const userTenantId = localStorage.getItem('simpliview_user_tenant_id');
    
    const previousTenantId = activeTenantId;
    
    let newTenantId, newTenantName, newIsImpersonating;
    
    if (impersonatedId) {
      newTenantId = impersonatedId;
      newTenantName = impersonatedName || 'Selected Tenant';
      newIsImpersonating = true;
    } else if (userTenantId) {
      newTenantId = userTenantId;
      newTenantName = user?.tenant?.name || user?.tenant_name || 'Your Tenant';
      newIsImpersonating = false;
    } else {
      newTenantId = null;
      newTenantName = null;
      newIsImpersonating = false;
    }
    
    // PART A & E: CLEAR ALL DATA ON TENANT SWITCH
    if (previousTenantId !== newTenantId && previousTenantId !== null) {
      console.log('[TenantContext] Tenant switched from', previousTenantId, 'to', newTenantId);
      
      // PART A: Clear ALL cached data from localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.startsWith('cache_') || 
          key.startsWith('data_') ||
          key.startsWith('agenticPM_') ||
          key.includes('_filter') ||
          key.includes('_state') ||
          key.includes('lastFetched')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`[TenantContext] Cleared ${keysToRemove.length} cached items from localStorage`);
      
      // PART B: Increment data version to force all components to refetch
      globalDataVersion++;
      setDataVersion(globalDataVersion);
      
      // Dispatch event to notify all components to clear their cached data
      window.dispatchEvent(new CustomEvent('tenantDataClear', { 
        detail: { 
          previousTenantId, 
          newTenantId,
          reason: 'tenant_switch',
          dataVersion: globalDataVersion
        } 
      }));
    }
    
    setActiveTenantId(newTenantId);
    setActiveTenantName(newTenantName);
    setIsImpersonating(newIsImpersonating);
  }, [user, activeTenantId]);

  // Initialize and listen for context changes
  useEffect(() => {
    handleTenantContextChange();
    
    window.addEventListener('tenantContextChanged', handleTenantContextChange);
    window.addEventListener('storage', handleTenantContextChange);
    
    return () => {
      window.removeEventListener('tenantContextChanged', handleTenantContextChange);
      window.removeEventListener('storage', handleTenantContextChange);
    };
  }, [handleTenantContextChange]);

  useEffect(() => {
    if (isAuthenticated && user?.tenant_id) {
      loadTenantData(user.tenant_id);
    } else {
      // Default features for unauthenticated or demo mode
      setFeatures({
        manual_editing: true, // Default to true for demo
        exec_dash: true,
        pmo_dash: true,
        pm_dash: true,
        upload_center: true,
        basic_reports: true,
        advanced_reports: true,
        overrides: true,
        audit_logs: true
      });
      setHierarchyConfig({ enable_story_layer: true });
      setLoading(false);
    }
  }, [isAuthenticated, user?.tenant_id]);

  const loadTenantData = async (tenantId) => {
    try {
      setLoading(true);
      const [limitsRes, hierarchyRes] = await Promise.all([
        api.getTenantLimits(tenantId),
        api.getHierarchyConfig(tenantId).catch(() => ({ data: { hierarchy_config: { enable_story_layer: true } } }))
      ]);
      
      setTenant({
        id: tenantId,
        tier: limitsRes.data.tier,
        currency: limitsRes.data.currency
      });
      setFeatures(limitsRes.data.features || {});
      setLimits(limitsRes.data.limits || {});
      setHierarchyConfig(hierarchyRes.data.hierarchy_config || { enable_story_layer: true });
    } catch (error) {
      console.error('Failed to load tenant data:', error);
      // Fallback to business tier features for demo
      setFeatures({
        manual_editing: true,
        exec_dash: true,
        pmo_dash: true,
        pm_dash: true,
        upload_center: true,
        basic_reports: true,
        advanced_reports: true,
        overrides: true,
        audit_logs: true
      });
      setHierarchyConfig({ enable_story_layer: true });
    } finally {
      setLoading(false);
    }
  };

  // Check if a feature is enabled for the current tenant
  const hasFeature = (featureKey) => {
    return features[featureKey] === true;
  };

  // Check if manual editing is enabled (Business/Enterprise only)
  const canEdit = () => {
    return features.manual_editing === true;
  };

  // Check if a limit allows an action
  const checkLimit = (limitKey, currentCount) => {
    const limit = limits[limitKey];
    if (limit === undefined || limit === -1) return true; // Unlimited
    return currentCount < limit;
  };

  // Get the current tier
  const getTier = () => {
    return tenant?.tier || 'business';
  };

  // Get the tenant's currency symbol
  const getCurrency = () => {
    // Check settings first, then fall back to tenant.currency, default to USD
    const currencyCode = tenant?.settings?.currency_code || tenant?.currency || 'USD';
    const symbols = { USD: '$', GBP: '£', EUR: '€', ZAR: 'R', AUD: 'A$', CAD: 'C$' };
    return symbols[currencyCode] || '$';
  };
  
  // Get the tenant's currency code (ISO 4217)
  const getCurrencyCode = () => {
    return tenant?.settings?.currency_code || tenant?.currency || 'USD';
  };

  // Check if on a lower tier (for upgrade prompts)
  const isLowerTier = () => {
    const tier = getTier();
    return tier === 'starter' || tier === 'growth';
  };

  // Check if story layer is enabled
  const isStoryLayerEnabled = () => {
    return hierarchyConfig?.enable_story_layer === true;
  };

  // Get hierarchy config
  const getHierarchyConfig = () => {
    return hierarchyConfig;
  };
  
  // TENANT ISOLATION: Check if tenant context is available
  const hasTenantContext = () => {
    return !!activeTenantId;
  };
  
  // Get active tenant context info
  const getActiveTenantContext = () => {
    return {
      id: activeTenantId,
      name: activeTenantName,
      isImpersonating
    };
  };

  const value = {
    tenant,
    features,
    limits,
    hierarchyConfig,
    loading,
    hasFeature,
    canEdit,
    checkLimit,
    getTier,
    getCurrency,
    getCurrencyCode,
    isLowerTier,
    isStoryLayerEnabled,
    getHierarchyConfig,
    refreshTenant: () => user?.tenant_id && loadTenantData(user.tenant_id),
    // TENANT ISOLATION: New exports
    activeTenantId,
    activeTenantName,
    isImpersonating,
    hasTenantContext,
    getActiveTenantContext,
    dataVersion, // PART B: Use this in useEffect deps to force refetch
    // TENANT ISOLATION: Add tenantId as an alias for activeTenantId (for backward compatibility)
    tenantId: activeTenantId,
    // PART A: Helper to clear data on tenant switch
    clearTenantData: () => {
      // Clear any tenant-specific localStorage
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('cache_') || key.startsWith('data_') || key.startsWith('agenticPM_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('[TenantContext] Cleared cached tenant data');
    }
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export default TenantContext;
